import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, mixins } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { ChipDownContainer, DomainsChipDownContainer } from "@similarweb/ui-components";
import { ListItemWebsite } from "@similarweb/ui-components";
import { PlainTooltip } from "@similarweb/ui-components";
import { SearchInput } from "@similarweb/ui-components";
import { CategoryItem } from "app/components/React/CategoriesDropdown/CategoryDropdown";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { i18nFilter } from "app/filters/i18nFilter";
import { categoryClassIconFilter } from "app/filters/ngFilters";
import {
    Separator,
    Title,
} from "app/pages/website-analysis/audience-interests/single/AudienceInterestsTableTop/AudienceInterestsTableTopStyles";
import { DownloadExcelContainer, SearchContainer } from "app/pages/workspace/StyledComponent";
import type { FunctionComponent } from "react";
import { allTrackers } from "app/services/track/track";
import { PrimaryBoxTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";
import styled from "styled-components";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { getExcelButtonText } from "app/components/React/ExcelButton/getExcelButtonText";

const StyledLabel = styled.div`
    ${mixins.setFont({ $color: colorsPalettes.carbon[500], $weight: 300, $size: 16 })};
`;
const TopStyled = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 12px;
`;
const TopStyledLeft = styled.div`
    display: flex;
    align-items: center;
`;
const ChipWrap = styled.div`
    padding-left: 8px;

    [data-automation-icon-name="arrow"] svg {
        width: 10px;
        height: 5px;
    }
`;
const CategoryItemContainer = styled(CategoryItem)`
    font-weight: 400;
`;
const Right = styled.div`
    flex-grow: 0;
    display: flex;
    align-items: center;
    margin-left: 10px;
`;
export const CompetitiveLandscapeTableTop: FunctionComponent<any> = (props) => {
    const i18n = i18nFilter();
    const isPaidExcelPermitted = isPaidExcelAllowed();
    const categoryClassIcon = categoryClassIconFilter();
    const {
        onSelectCategory: onSelectCategoryProps,
        tableColumns,
        onClickToggleColumns,
        selectedSite,
        excelLink,
        downloadExcelPermitted,
        onChange,
        searchValue,
        isLoadingData,
        selectedCategory,
        selectedCategoryId,
        onClearAll,
        selectedSites,
        onSelectSite,
        onClearSite,
        isCompare,
        allCategories,
        totalCount,
    } = props;
    const selectedSiteFilter = selectedSites.find((x) => x.value === selectedSite) || selectedSites[0];
    const selectedSiteFilterText = selectedSiteFilter.label;

    const getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };
    const onColumnToggle = (key) => {
        onClickToggleColumns(parseInt(key, 10));
    };
    const excelDownloadUrl = downloadExcelPermitted ? excelLink : "";
    const trackExcelDownload = () => {
        allTrackers.trackEvent("Download", "submit-ok", "Table/Excel");
    };
    const getCategoriesOptions = () => {
        const items = allCategories.reduce((result, category) => {
            if (category.children.length > 0) {
                return [
                    ...result,
                    convertCategory(category),
                    ...category.children.map((child) => convertCategory(child, category.id)),
                ];
            } else {
                return [...result, convertCategory(category)];
            }
        }, []);

        return items.map((item, index) => {
            return (
                <CategoryItemContainer
                    {...item}
                    key={index}
                    selected={item.forApi === selectedCategoryId}
                />
            );
        });
    };
    const convertCategory = ({ text, children = [], id }, parentId = null) => {
        return {
            text,
            id,
            isCustomCategory: false,
            isChild: !children || children.length === 0,
            icon: categoryClassIcon(id),
            forApi: `${parentId ? `${parentId}~` : ``}${id}`,
        };
    };
    const onSelectCategory = (category) => {
        const value = category ? category.forApi.replace("~", " > ").replace("_", " ") : "Clear Filter";
        allTrackers.trackEvent("Drop Down", "click", `Table/Category/${value}`);
        onSelectCategoryProps(category && category.id);
    };
    const onClearCategory = () => {
        onSelectCategory(null);
    };
    return isLoadingData ? null : (
        <>
            <Title>
                <PrimaryBoxTitle>
                    {i18n("analysis.common.competitors.similar.title", { site: selectedSite })}
                    <PlainTooltip
                        tooltipContent={i18n("analysis.common.competitors.similar.title.tooltip", {
                            site: selectedSite,
                        })}
                    >
                        <div style={{ display: "inline-block", marginLeft: 6 }}>
                            <SWReactIcons
                                iconName="info"
                                size="xs"
                            />
                        </div>
                    </PlainTooltip>
                </PrimaryBoxTitle>
            </Title>
            <Separator />
            <TopStyled>
                <TopStyledLeft>
                    <StyledLabel>{i18n("traffic.search.phrases.page.filterby.title")}</StyledLabel>
                    {isCompare ? (
                        <ChipWrap>
                            <DomainsChipDownContainer
                                onClick={onSelectSite}
                                selectedIds={{ [selectedSiteFilter.value]: true }}
                                selectedDomainText={selectedSiteFilterText}
                                selectedDomainIcon={selectedSiteFilter.icon}
                                onCloseItem={onClearSite}
                                buttonText={selectedSiteFilter.label}
                                noResultsMessage={i18n("dropdown.no.results")}
                            >
                                {selectedSites.map((item, index) => {
                                    const { label, icon } = item;
                                    return (
                                        <ListItemWebsite
                                            key={index}
                                            text={label}
                                            img={icon}
                                        />
                                    );
                                })}
                            </DomainsChipDownContainer>
                        </ChipWrap>
                    ) : null}
                    {allCategories.length > 0 ? (
                        <ChipWrap>
                            <ChipDownContainer
                                width={340}
                                onClick={onSelectCategory}
                                selectedText={selectedCategory ?? ""}
                                onCloseItem={onClearCategory}
                                buttonText={i18n("common.category.all")}
                                searchPlaceHolder={i18n("home.dashboards.wizard.filters.searchCategory")}
                                tooltipDisabled={true}
                                hasSearch={true}
                                noResultsMessage={i18n("dropdown.no.results")}
                            >
                                {getCategoriesOptions()}
                            </ChipDownContainer>
                        </ChipWrap>
                    ) : null}
                </TopStyledLeft>
                <Button
                    type="flat"
                    onClick={onClearAll}
                >
                    {i18n("forms.buttons.clearall.text")}
                </Button>
            </TopStyled>
            <SearchContainer>
                <SearchInput
                    disableClear={true}
                    defaultValue={searchValue}
                    debounce={400}
                    onChange={onChange}
                    placeholder={i18n("forms.search.placeholder")}
                />
                <Right>
                    <FlexRow>
                        <DownloadExcelContainer>
                            <DownloadButtonMenu
                                Excel={true}
                                iconNameProps={!downloadExcelPermitted ? "excel-download" : null}
                                downloadUrl={excelDownloadUrl}
                                exportFunction={trackExcelDownload}
                                excelLocked={!downloadExcelPermitted}
                                PaidExcel={isPaidExcelPermitted}
                                totalCount={totalCount}
                                buttonText={getExcelButtonText()}
                            />
                        </DownloadExcelContainer>
                        <div>
                            <ColumnsPickerLite
                                {...getColumnsPickerLiteProps()}
                                translations={{
                                    tooltip: i18n("workspace.sales.column.picker"),
                                    getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                        i18n("common.columnsPicker.selectedAmount", {
                                            selected: selectedValue,
                                            total: totalValue,
                                        }),
                                    mandatoryField: i18n("common.columnsPicker.mandatoryField"),
                                    apply: i18n("common.apply"),
                                }}
                            />
                        </div>
                    </FlexRow>
                </Right>
            </SearchContainer>
        </>
    );
};
